import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ProvinceBlock as ProvinceBlockType } from '@/types';
import { useGameStore } from '@/store/gameStore';
import { loadProvinceSVGPath, validateSVGPath, normalizeSVGPath } from '@/services/svgService';
import { getProvinceBlockSize, getProvinceSizeDescription } from '@/config/provinceSizes';
import { getAbbreviationDisplay, hasAlternativeAbbreviation, getFontSizeConfig } from '@/data/provincePinyin';
import clsx from 'clsx';

interface DraggableProvinceBlockProps {
  province: ProvinceBlockType;
  showName?: boolean;
  showAbbreviation?: boolean;      // 是否显示简称拼音
  isHinted?: boolean;
  onDragStart?: (province: ProvinceBlockType) => void;
  onDragEnd?: () => void;
}

export const DraggableProvinceBlock: React.FC<DraggableProvinceBlockProps> = ({
  province,
  showName = false,
  showAbbreviation = true,        // 默认显示简称拼音
  isHinted = false,
  onDragStart,
  onDragEnd
}) => {
  const blockRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [svgPath, setSvgPath] = useState<string>('');
  const [svgLoadStatus, setSvgLoadStatus] = useState<'loading' | 'loaded' | 'failed'>('loading');
  const [showAlternative, setShowAlternative] = useState(false); // 是否显示备用简称
  const { completedProvinces, getProvinceGeoData, showAbbreviations } = useGameStore();

  // 获取省份的动态大小配置
  const sizeConfig = getProvinceBlockSize(province.name);
  const sizeDescription = getProvinceSizeDescription(province.name);
  
  // 获取简称拼音显示文本
  const abbreviationText = getAbbreviationDisplay(province.name, showAlternative);
  const hasAlternative = hasAlternativeAbbreviation(province.name);
  
  // 获取字体大小配置
  const fontConfig = getFontSizeConfig('large'); // 使用大字体
  
  // 切换简称显示（如果有备用简称）
  const toggleAbbreviation = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasAlternative) {
      setShowAlternative(!showAlternative);
    }
  };

  // 组件挂载时加载SVG
  useEffect(() => {
    loadProvinceSVG();
  }, [province.name]);

  // 加载省份SVG的主函数
  const loadProvinceSVG = async () => {
    setSvgLoadStatus('loading');
    
    try {
      console.log(`🎨 开始为 ${province.name} 生成SVG路径`);
      
      // 第一优先级：外部SVG文件
      const externalSVGPath = await loadProvinceSVGPath(province.name);
      if (externalSVGPath && validateSVGPath(externalSVGPath)) {
        const normalizedPath = normalizeSVGPath(externalSVGPath);
        setSvgPath(normalizedPath);
        setSvgLoadStatus('loaded');
        console.log(`✅ 成功使用外部SVG文件: ${province.name}`);
        return;
      }

      // 第二优先级：从GeoJSON数据生成
      const geoJsonPath = generateProvincePathFromGeoJSON();
      if (geoJsonPath && validateSVGPath(geoJsonPath)) {
        setSvgPath(geoJsonPath);
        setSvgLoadStatus('loaded');
        console.log(`✅ 成功从GeoJSON生成SVG: ${province.name}`);
        return;
      }

      // 第三优先级：预定义形状
      const defaultPath = getDefaultProvinceShape(province.id);
      setSvgPath(defaultPath);
      setSvgLoadStatus('loaded');
      console.log(`⚠️ 使用默认形状: ${province.name}`);
      
    } catch (error) {
      console.error(`❌ 生成SVG路径失败: ${province.name}`, error);
      
      // 最后的备用方案
      const fallbackPath = getDefaultProvinceShape(province.id);
      setSvgPath(fallbackPath);
      setSvgLoadStatus('failed');
    }
  };

  // 获取省份的GeoJSON数据
  const getProvinceGeoData_Local = () => {
    return getProvinceGeoData(province.id);
  };

  // 从GeoJSON数据生成SVG路径
  const generateProvincePathFromGeoJSON = (): string | null => {
    const geoData = getProvinceGeoData_Local();
    
    if (!geoData || !geoData.geometry || !geoData.geometry.coordinates || !geoData.geometry.coordinates[0]) {
      return null;
    }

    const coordinates = geoData.geometry.coordinates[0];
    if (!Array.isArray(coordinates) || coordinates.length === 0) {
      return null;
    }

    try {
      // 将地理坐标转换为SVG路径（简化处理）
      const bounds = {
        minX: Math.min(...coordinates.map((coord: number[]) => coord[0])),
        maxX: Math.max(...coordinates.map((coord: number[]) => coord[0])),
        minY: Math.min(...coordinates.map((coord: number[]) => coord[1])),
        maxY: Math.max(...coordinates.map((coord: number[]) => coord[1]))
      };

      const width = bounds.maxX - bounds.minX;
      const height = bounds.maxY - bounds.minY;

      if (width === 0 || height === 0) {
        return null;
      }

      // 简化坐标点（取每10个点中的1个以减少复杂度）
      const simplifiedCoords = coordinates.filter((_: any, index: number) => index % 10 === 0);
      
      // 将坐标标准化到100x100的SVG视图框内
      const normalizedCoords = simplifiedCoords.map((coord: number[]) => [
        ((coord[0] - bounds.minX) / width) * 80 + 10,
        ((bounds.maxY - coord[1]) / height) * 80 + 10 // 翻转Y轴
      ]);

      if (normalizedCoords.length < 3) {
        return null;
      }

      let path = `M ${normalizedCoords[0][0]} ${normalizedCoords[0][1]}`;
      for (let i = 1; i < normalizedCoords.length; i++) {
        path += ` L ${normalizedCoords[i][0]} ${normalizedCoords[i][1]}`;
      }
      path += ' Z';

      return path;
    } catch (error) {
      console.warn(`从GeoJSON生成省份 ${province.name} 的SVG路径失败:`, error);
      return null;
    }
  };

  // 获取默认省份形状（备用方案）
  const getDefaultProvinceShape = (provinceId: string): string => {
    const shapes: Record<string, string> = {
      beijing: 'M 20 20 L 80 20 L 80 80 L 20 80 Z', // 方形
      shanghai: 'M 50 10 L 90 50 L 50 90 L 10 50 Z', // 菱形
      guangdong: 'M 10 30 Q 50 10 90 30 L 90 70 Q 50 90 10 70 Z', // 椭圆形
      sichuan: 'M 10 50 L 30 10 L 70 10 L 90 50 L 70 90 L 30 90 Z', // 六边形
      xinjiang: 'M 10 10 L 90 10 L 90 90 L 10 90 Z', // 大矩形
      tibet: 'M 15 15 L 85 15 L 85 85 L 15 85 Z', // 大方形
      heilongjiang: 'M 20 10 L 80 10 L 90 30 L 80 90 L 20 90 L 10 30 Z', // 不规则六边形
      hainan: 'M 30 20 Q 70 10 80 50 Q 70 90 30 80 Q 20 50 30 20 Z', // 椭圆
      default: 'M 25 15 L 75 15 L 85 35 L 75 85 L 25 85 L 15 35 Z' // 默认形状
    };
    
    return shapes[provinceId] || shapes.default;
  };

  const handleDragStart = (event: React.DragEvent) => {
    if (province.isLocked) return;
    
    setIsDragging(true);
    onDragStart?.(province);
    
    // 设置拖拽数据
    event.dataTransfer.setData('text/plain', province.id);
    event.dataTransfer.effectAllowed = 'move';
    
    // 创建自定义拖拽图像
    const dragImage = blockRef.current?.cloneNode(true) as HTMLElement;
    if (dragImage) {
      dragImage.style.transform = 'rotate(5deg)';
      dragImage.style.opacity = '0.8';
      document.body.appendChild(dragImage);
      event.dataTransfer.setDragImage(dragImage, 50, 50);
      setTimeout(() => {
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage);
        }
      }, 0);
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd?.();
  };

  const isCompleted = completedProvinces.includes(province.id);

  // 动画变体
  const variants = {
    idle: {
      scale: 1,
      rotate: 0,
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    dragging: {
      scale: 1.1,
      rotate: 5,
      boxShadow: '0 8px 25px rgba(0,0,0,0.3)',
      zIndex: 1000
    },
    completed: {
      scale: 0.9,
      opacity: 0.6,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    hinted: {
      scale: [1, 1.05, 1],
      boxShadow: [
        '0 4px 12px rgba(59, 130, 246, 0.3)',
        '0 8px 20px rgba(59, 130, 246, 0.6)',
        '0 4px 12px rgba(59, 130, 246, 0.3)'
      ]
    }
  };

  const getCurrentVariant = () => {
    if (isCompleted) return 'completed';
    if (isDragging) return 'dragging';
    if (isHinted) return 'hinted';
    return 'idle';
  };

  return (
    <motion.div
      ref={blockRef}
      className={clsx(
        'relative rounded-xl border-2 cursor-pointer select-none transition-colors duration-200',
        sizeConfig.className, // 动态大小类名
        {
          'border-blue-400 bg-blue-50': !isCompleted && !isHinted,
          'border-green-400 bg-green-50': isCompleted,
          'border-yellow-400 bg-yellow-50': isHinted && !isCompleted,
          'cursor-not-allowed': province.isLocked || isCompleted,
          'cursor-grab': !province.isLocked && !isCompleted,
          'cursor-grabbing': isDragging
        }
      )}
      variants={variants}
      animate={getCurrentVariant()}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30
      }}
      style={{
        transform: `scale(${sizeConfig.scale})`, // 动态缩放
        transformOrigin: 'center'
      }}
      title={`${province.name} (${sizeDescription})`} // 添加提示信息
      draggable={!province.isLocked && !isCompleted}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {/* 省份轮廓SVG */}
      <div className="w-full h-full flex items-center justify-center p-2 relative">
        {svgLoadStatus === 'loading' ? (
          // 加载状态
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          // SVG内容
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 100"
            className="pointer-events-none"
          >
            <path
              d={svgPath}
              fill="currentColor"
              stroke="none"
              className={clsx(
                'transition-colors duration-200',
                {
                  'text-blue-500': !isCompleted && !isHinted,
                  'text-green-500': isCompleted,
                  'text-yellow-500': isHinted && !isCompleted
                }
              )}
            />
          </svg>
        )}
        
        {/* 简称拼音显示在拼图块内部 */}
        {showAbbreviations && abbreviationText && (
          <motion.div
            className={clsx(
              'absolute inset-0 flex flex-col items-center justify-center pointer-events-none',
              'text-white font-bold',
              fontConfig.shadowClass
            )}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className={clsx(fontConfig.containerClass, 'leading-tight text-center')}>
              {abbreviationText.split(' ').map((part, index) => (
                <div key={index} className={clsx(
                  index === 0 ? fontConfig.abbreviationClass : fontConfig.pinyinClass
                )}>
                  {part}
                </div>
              ))}
            </div>
          </motion.div>
        )}
        
        {/* 备用简称指示器 */}
        {hasAlternative && showAbbreviations && (
          <motion.button
            className={clsx(
              'absolute -top-1 -right-1 w-4 h-4 rounded-full',
              'bg-yellow-400 text-yellow-800 text-xs font-bold',
              'flex items-center justify-center cursor-pointer',
              'hover:bg-yellow-300 transition-colors duration-200',
              'shadow-sm z-10'
            )}
            onClick={toggleAbbreviation}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            title={showAlternative ? '点击显示主要简称' : '点击显示备用简称'}
          >
            ⇄
          </motion.button>
        )}
      </div>

      {/* SVG来源指示器 (开发模式) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-0 left-0 w-2 h-2 rounded-full" 
             title={`SVG来源: ${svgLoadStatus === 'loaded' ? '外部文件' : svgLoadStatus === 'failed' ? '默认形状' : '加载中'}`}>
          <div className={clsx('w-full h-full rounded-full', {
            'bg-green-400': svgLoadStatus === 'loaded',
            'bg-red-400': svgLoadStatus === 'failed',
            'bg-yellow-400': svgLoadStatus === 'loading'
          })}></div>
        </div>
      )}

      {/* 省份名称标签 */}
      {(showName || isCompleted) && (
        <motion.div
          className="absolute -bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <span className="text-xs font-medium text-gray-700 bg-white px-2 py-1 rounded-md shadow-sm whitespace-nowrap border">
            {province.name}
          </span>
        </motion.div>
      )}

      {/* 省份大小指示器 (开发模式) */}
      {typeof window !== 'undefined' && window.location.hostname === 'localhost' && (
        <div className="absolute top-1 left-1 w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold shadow-sm"
             title={sizeDescription}>
          <div className={clsx('w-full h-full rounded-full flex items-center justify-center text-white text-xs', {
            'bg-red-500': sizeConfig.category === 'extra-large',
            'bg-orange-500': sizeConfig.category === 'large', 
            'bg-blue-500': sizeConfig.category === 'medium',
            'bg-green-500': sizeConfig.category === 'small',
            'bg-purple-500': sizeConfig.category === 'tiny'
          })}>
            {sizeConfig.category === 'extra-large' ? 'XL' :
             sizeConfig.category === 'large' ? 'L' :
             sizeConfig.category === 'medium' ? 'M' :
             sizeConfig.category === 'small' ? 'S' : 'XS'}
          </div>
        </div>
      )}

      {/* 拖拽提示 */}
      {isDragging && (
        <motion.div
          className="absolute -top-10 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-md shadow-sm border border-blue-200">
            拖拽到地图
          </span>
        </motion.div>
      )}

      {/* 完成状态指示 */}
      {isCompleted && (
        <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
          <span className="text-white text-xs">✓</span>
        </div>
      )}

      {/* 提示状态指示 */}
      {isHinted && !isCompleted && (
        <motion.div
          className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          <span className="text-white text-xs">💡</span>
        </motion.div>
      )}

      {/* 悬停效果 */}
      {!isCompleted && !province.isLocked && (
        <motion.div
          className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 opacity-0 pointer-events-none"
          whileHover={{ opacity: 0.1 }}
          transition={{ duration: 0.2 }}
        />
      )}
    </motion.div>
  );
};
